import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, contactsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users'),
        api.get('/admin/contacts'),
      ]);
      setStats(statsRes.data.data);
      setUsers(usersRes.data.data || []);
      setContacts(contactsRes.data.data || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#f9fafb' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 4,
            color: '#f97316',
          }}
        >
          Admin Dashboard
        </Typography>

        {/* Statistics */}
        {stats && (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                      {stats.overview?.totalUsers || stats.totalUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                    {stats.recentActivity && (
                      <Typography variant="caption" color="text.secondary">
                        +{stats.recentActivity.newUsersLast30Days} this month
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                      {stats.overview?.totalEvents || stats.totalEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Events
                    </Typography>
                    {stats.recentActivity && (
                      <Typography variant="caption" color="text.secondary">
                        +{stats.recentActivity.newEventsLast30Days} this month
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                      {stats.overview?.totalPosts || stats.totalPosts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Posts
                    </Typography>
                    {stats.recentActivity && (
                      <Typography variant="caption" color="text.secondary">
                        +{stats.recentActivity.newPostsLast30Days} this month
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                      {stats.overview?.unreadContacts || stats.unreadContacts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unread Messages
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {stats.upcomingEvents && stats.upcomingEvents.length > 0 && (
              <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Upcoming Events
                  </Typography>
                  <Grid container spacing={2}>
                    {stats.upcomingEvents.map((event) => (
                      <Grid item xs={12} sm={6} md={4} key={event._id}>
                        <Box
                          sx={{
                            p: 2,
                            border: '1px solid #333',
                            borderRadius: 2,
                            '&:hover': { borderColor: '#f97316' },
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {event.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {event.attendees?.length || 0} attendees
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Users Table */}
        <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Users
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.slice(0, 10).map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          sx={{
                            backgroundColor: user.role === 'admin' ? '#f97316' : '#6b7280',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: user.isActive ? '#4caf50' : '#f44336',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Contact Messages */}
        <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Recent Contact Messages
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.slice(0, 10).map((contact) => (
                    <TableRow key={contact._id}>
                      <TableCell>{contact.name}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={contact.isRead ? 'Read' : 'New'}
                          size="small"
                          sx={{
                            backgroundColor: contact.isRead ? '#6b7280' : '#f97316',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminPanel;

